import {
  DepositBookingContext,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshot,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { of, type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Patient } from '../patient/patient';

/**
 * Owns how a staff-taken booking is classified into a deposit booking context,
 * so the deposit policy sees the same new-vs-existing patient answer whether it
 * is asked from a synchronous backend flow or a live booking form.
 */
export class StaffDepositBookingContext {
  /**
   * Resolves the booking context for a phone/walk-in booking taken by staff. A
   * patient that does not exist yet is being created inline by this booking, so
   * it always counts as a new patient.
   */
  static async resolve(
    patient?: IReffable<IPatient>
  ): Promise<DepositBookingContext> {
    if (!patient) {
      return DepositBookingContext.StaffNewPatient;
    }
    const isNewPatient = await snapshot(Patient.isNewPatient$(patient));
    return isNewPatient
      ? DepositBookingContext.StaffNewPatient
      : DepositBookingContext.StaffExistingPatient;
  }

  /**
   * Observable form of {@link StaffDepositBookingContext.resolve}. Emits
   * `undefined` first while an existing patient's appointment history is
   * loading. Consumers must preserve that unresolved state rather than treating
   * it as a booking context for which no deposit is required.
   */
  static resolve$(
    patient?: WithRef<IPatient>
  ): Observable<DepositBookingContext | undefined> {
    if (!patient) {
      return of(DepositBookingContext.StaffNewPatient);
    }
    return Patient.isNewPatient$(patient).pipe(
      map((isNewPatient) =>
        isNewPatient
          ? DepositBookingContext.StaffNewPatient
          : DepositBookingContext.StaffExistingPatient
      ),
      startWith(undefined)
    );
  }
}
